import React from 'react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';
import classNameBind from 'classnames/bind';

import { withTranslation } from '../Translator';
import Icon from '../Icon';
import Label from '../Label';
import Stack from '../Stack';
import Popover from '../Popover';
import { KEYS } from '../../utils/methods/keyboardKeys';
import styles from './MultiSelect.module.css';
import SelectionListWrapper from '../SelectionList/components/SelectionListWrapper';

const bindClasses = classNameBind.bind(styles);

const getValue = item => get(item, 'value');
const getItem = item => ({
  name: item.name,
  value: item.id,
  caption: item.caption,
});

class MultiSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selected: get(props.value, 0, null),
    };
  }

  getItemsAsList = () => {
    const { options } = this.props;

    return flatten(
      options.map(elem => elem.items),
    );
  }

  getAvailableItems = () => (
    this.getItemsAsList().filter(item => item.disabled !== true)
  );

  getSelectedItemId = () => (
    get(this.getItemsAsList().find(item => isEqual(item.value, this.state.selected)), 'id')
  )

  getSelectedItemValues = () => (
    this.props.sortByHistory
      ? this.props.value.map(el => this.getItemsAsList().find(el2 => el2.value === el)).map(getItem)
      : this.getItemsAsList()
        .filter(item => this.props.value.includes(item.value))
        .map(getItem)
  );

  getItemValues = () => (
    this.props.options
      .map(group => ({
        group_id: group.id,
        group_label: group.name,
        subItems: group.items.filter(el => !el.notFoundViaSearch).map(getItem),
      }))
      .filter(group => group.subItems.length)
  );

  getNewValue = (selectedValue) => {
    const { single, value } = this.props;

    if (!single) {
      const index = value.findIndex(iValue => iValue === selectedValue);

      if (index === -1) {
        return [...value, selectedValue];
      }

      return [
        ...value.slice(0, index),
        ...value.slice(index + 1),
      ];
    }

    return [selectedValue];
  }

  handleListChange = (newValue) => {
    const values = newValue.map(item => item.value);

    this.handleChange(
      this.props.sortByHistory
        ? values.map(el => this.getItemsAsList().find(el2 => el2.id === el).value)
        : this.getItemsAsList().filter(item => values.includes(item.id)).map(item => item.value),
    );
  }

  handleChange = (newValue) => {
    const { value, onChange, name } = this.props;

    if (!isEqual(newValue, value)) {
      onChange({
        target: {
          name,
          value: newValue,
        },
      });
    }
  }

  handleKeyPress = (event) => {
    const { value } = this.props;
    const { selected } = this.state;
    const selectedValue = (selected || get(value, 0));

    if (event.key === KEYS.ENTER) {
      event.preventDefault();
      this.handleChange(this.getNewValue(selectedValue));
      if (event.target.form) {
        event.target.form.removeEventListener('keypress', this.handleKeyPress);
      }
    }
  }

  handleKeyDown = (event, onToggle, isOpen) => {
    const { single, value, enableSearch } = this.props;
    const { selected } = this.state;
    const items = this.getAvailableItems();
    const selectedValue = selected || get(value, 0);

    if ([KEYS.TAB, KEYS.ESC].includes(event.key) && isOpen) {
      onToggle();
    }

    if (event.key === KEYS.SPACE && !enableSearch) {
      event.preventDefault();
      this.handleChange(this.getNewValue(selectedValue));
      if (!isOpen || single) {
        onToggle();
      }
    }

    if (!event.target.form && event.key === KEYS.ENTER) {
      event.preventDefault();
      this.handleChange(this.getNewValue(selectedValue));
      if (!isOpen || single) {
        onToggle();
      }
    }

    if ([KEYS.DOWN, KEYS.UP].includes(event.key)) {
      event.preventDefault();
      if (!isOpen) {
        if (event.target.form) {
          event.target.form.addEventListener('keypress', this.handleKeyPress);
        }

        return onToggle();
      }

      let index = items.findIndex(item => getValue(item) === selectedValue);

      if (event.key === KEYS.DOWN) {
        index += 1;
      } else if (event.key === KEYS.UP) {
        index -= 1;
      }

      if (this.isInsideTheRange(index, items)) {
        this.setState({
          selected: items[index].value,
        });
      }
    }

    return null;
  }

  handleHover = (id) => {
    const items = this.getAvailableItems();

    this.setState({
      selected: get(items.find(el => el.id === id), 'value', items[0].value),
    });
  }

  handleSelection = (dir) => {
    const { value } = this.props;
    const { selected } = this.state;
    const selectedValue = selected || get(value, 0);
    const items = this.getItemsAsList();
    const index = items.findIndex(item => (getValue(item) === selectedValue)) + dir;

    if (this.isInsideTheRange(index, items)) {
      this.setState({
        selected: selectedValue,
      });
    }
  }

  isInsideTheRange = (index, items) => (
    index >= 0 && index < items.length
  )

  render() {
    const {
      value,
      getSelectedName,
      onToggle,
      toggleRef,
      single,
      customMaxWidth,
      disabled,
      error,
      touched,
      afterListElement,
      search,
      enableSearch,
      onSearchChange,
      onClose,
      zeroState,
      type,
      clearMinWidth,
      wrap,
    } = this.props;
    const shouldCloseSelect = () => single;

    const activator = (
      <div className={bindClasses('activator', { disabled })}>
        <div
          className={bindClasses('multiSelect', { disabled, error: error && touched })}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={(event) => { if (!disabled) { onToggle(event); } }}
          ref={toggleRef}
          data-ppuc-id="MultiSelect.button"
        >
          <Stack spacing="narrow" verticalAlignment="center">
            <Stack.Item fill>
              <Label
                type={type === 'subduedSmall' ? 'small' : 'default'}
                noSelect
              >
                {getSelectedName(value) || zeroState}
              </Label>
            </Stack.Item>
            <Stack.Item>
              <Icon name="chevron-down" />
            </Stack.Item>
          </Stack>
        </div>
      </div>
    );

    return (
      <div data-ppuc-id="MultiSelect" className={bindClasses('wrapper', type, { clearMinWidth })}>
        <Popover
          activatorElement={activator}
          childToggle={{ onItemSelected: shouldCloseSelect }}
          noPadding
          fixedWidth={type !== 'subduedSmall' ? !clearMinWidth : false}
          minWidth={type === 'subduedSmall'}
          customMaxWidth={customMaxWidth}
          disabled={disabled}
          onSelection={this.handleSelection}
          onKeyDown={this.handleKeyDown}
          dataId="MultiSelect.popover"
          focusOrder={enableSearch ? ['content'] : ['reference']}
        >
          <SelectionListWrapper
            items={this.getItemValues()}
            onItemSelected={this.handleListChange}
            value={this.getSelectedItemValues()}
            fixedHeight
            multi={!single}
            highlight={this.getSelectedItemId()}
            afterListElement={afterListElement}
            onSearchKeyDown={this.handleKeyDown}
            search={search}
            enableSearch={enableSearch}
            onSearchChange={onSearchChange}
            onClose={onClose}
            onHoverElement={this.handleHover}
            wrap={wrap}
          />
        </Popover>
      </div>
    );
  }
}

MultiSelect.defaultProps = {
  value: [],
  onToggle: () => {},
  toggleRef: () => {},
  onChange: () => {},
  single: false,
  disabled: false,
  sortByHistory: false,
  afterListElement: null,
  search: '',
  enableSearch: false,
  onSearchChange: () => {},
  onClose: () => {},
  zeroState: '',
  type: 'default',
  clearMinWidth: false,
  wrap: false,
};

const withTranslate = withTranslation('components')(MultiSelect);
withTranslate.displayName = 'MultiSelect';

export default withTranslate;
